// DeepSeek API service for AI Drill question generation
const DEEPSEEK_API_KEY = 'sk-6d1c0e29faa046ba857b2fcc15abd3b3';
const DEEPSEEK_API_URL = 'https://api.deepseek.com/v1/chat/completions';

class DeepSeekDrillApiService {
  constructor() {
    this.apiKey = DEEPSEEK_API_KEY;
    this.baseUrl = DEEPSEEK_API_URL;
  }

  async generateDrillQuestions(originalQuestion, numberOfDrills = 5, onProgress = null) {
    const startTime = Date.now();
    let tokensUsed = 0;
    let estimatedTokens = 0;

    try {
      // Estimate tokens based on question complexity
      estimatedTokens = this.estimateTokens(originalQuestion, numberOfDrills);
      
      if (onProgress) {
        onProgress({
          stage: 'preparing',
          message: '準備生成練習題...',
          tokensUsed: 0,
          estimatedTokens: estimatedTokens,
          timeElapsed: 0
        });
      }

      const prompt = this.createRealDrillPrompt(originalQuestion, numberOfDrills);
      
      if (onProgress) {
        onProgress({
          stage: 'analyzing',
          message: '分析原始題目結構...',
          tokensUsed: 0,
          estimatedTokens: estimatedTokens,
          timeElapsed: Math.floor((Date.now() - startTime) / 1000)
        });
      }

      // Simulate some processing time for better UX
      await this.delay(1000);
      
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: this.getRealSystemPrompt()
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.3, // Reduced temperature for more consistent results
          max_tokens: 4000, // Increased for more detailed solutions
        }),
      });

      if (onProgress) {
        onProgress({
          stage: 'generating',
          message: '正在生成 5 道練習題...',
          tokensUsed: Math.floor(estimatedTokens * 0.3),
          estimatedTokens: estimatedTokens,
          timeElapsed: Math.floor((Date.now() - startTime) / 1000)
        });
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('DeepSeek Drill API Error Response:', errorText);
        throw new Error(`DeepSeek Drill API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('DeepSeek Drill API Response:', data);
      
      // Calculate actual tokens used
      tokensUsed = data.usage?.total_tokens || estimatedTokens;
      
      if (onProgress) {
        onProgress({
          stage: 'processing',
          message: '處理 AI 回應...',
          tokensUsed: tokensUsed,
          estimatedTokens: estimatedTokens,
          timeElapsed: Math.floor((Date.now() - startTime) / 1000)
        });
      }
      
      if (!data.choices || !data.choices[0] || !data.choices[0].message) {
        throw new Error('Invalid API response structure');
      }
      
      const content = data.choices[0].message.content;
      console.log('Raw drill content from API:', content);
      
      if (onProgress) {
        onProgress({
          stage: 'validating',
          message: '驗證題目完整性...',
          tokensUsed: tokensUsed,
          estimatedTokens: estimatedTokens,
          timeElapsed: Math.floor((Date.now() - startTime) / 1000)
        });
      }
      
             // Parse the JSON response with improved error handling
       let drillQuestionsData;
       try {
         drillQuestionsData = JSON.parse(content);
         console.log('Successfully parsed JSON:', drillQuestionsData);
       } catch (parseError) {
         console.error('JSON Parse Error:', parseError);
         console.error('Raw content from API:', content);
         
         // Try to extract and fix JSON from the response
         try {
           // Clean up common JSON formatting issues
           let cleanedContent = content
             .replace(/^\s*\{?\s*\{/, '{') // Remove duplicate opening braces
             .replace(/\}\s*\}\s*$/, '}') // Remove duplicate closing braces
             .replace(/,\s*}/g, '}') // Remove trailing commas
             .replace(/,\s*]/g, ']') // Remove trailing commas in arrays
             .replace(/,\s*,/g, ',') // Remove duplicate commas
             .replace(/\[\s*,/g, '[') // Remove leading commas in arrays
             .replace(/,\s*\]/g, ']'); // Remove trailing commas in arrays
           
           // Look for JSON object in the cleaned response
           const jsonMatch = cleanedContent.match(/\{[\s\S]*\}/);
           if (jsonMatch) {
             const extractedJson = jsonMatch[0];
             console.log('Attempting to parse cleaned JSON:', extractedJson);
             drillQuestionsData = JSON.parse(extractedJson);
             console.log('Successfully parsed cleaned JSON:', drillQuestionsData);
           } else {
             throw new Error('No valid JSON found in response');
           }
         } catch (extractError) {
           console.error('Failed to extract and clean JSON:', extractError);
           throw new Error(`API returned invalid JSON format. Please try again. Raw response: ${content.substring(0, 200)}...`);
         }
       }
      
      // Validate and fix the generated questions
      const validatedData = this.validateAndFixDrillQuestions(drillQuestionsData, originalQuestion);
      
      const costIncurred = this.calculateCost(tokensUsed, data.usage);
      
      if (onProgress) {
        onProgress({
          stage: 'complete',
          message: '練習題生成完成！',
          tokensUsed: tokensUsed,
          estimatedTokens: estimatedTokens,
          timeElapsed: Math.floor((Date.now() - startTime) / 1000),
          costIncurred: costIncurred
        });
      }
      
      return {
        ...validatedData,
        metadata: {
          tokensUsed: tokensUsed,
          estimatedTokens: estimatedTokens,
          generationTime: Math.floor((Date.now() - startTime) / 1000),
          apiResponse: data,
          validationApplied: true,
          isRealAIGenerated: true,
          costIncurred: costIncurred
        }
      };
    } catch (error) {
      console.error('Error generating drill questions:', error);
      
      // Provide more specific error messages
      let userMessage = '生成練習題時發生錯誤，請稍後再試。';
      if (error.message.includes('JSON')) {
        userMessage = 'AI 回應格式錯誤，正在重試...';
      } else if (error.message.includes('API')) {
        userMessage = 'API 連接錯誤，請檢查網絡連接。';
      } else if (error.message.includes('token')) {
        userMessage = 'API 配額已用完，請稍後再試。';
      }
      
      throw new Error(userMessage + ' 錯誤詳情：' + error.message);
    }
  }

  getRealSystemPrompt() {
    return `You are an expert HKDSE Mathematics teacher. Your task is to create REAL mathematical drill questions that are:

1. MATHEMATICALLY ACCURATE: Every solution must be correct and verified
2. HKDSE STYLE: Questions must match HKDSE format and difficulty
3. EDUCATIONAL: Solutions should be step-by-step and educational
4. REAL CONTENT: Generate actual mathematical questions, NOT placeholder text
5. LaTeX FORMATTING: Use proper LaTeX for all mathematical expressions

CRITICAL REQUIREMENTS:
- Generate REAL mathematical questions with actual numbers and problems
- Ensure all calculations are mathematically correct
- Use proper LaTeX syntax: $\\frac{a}{b}$, $x^2$, $\\sqrt{x}$, $\\sin(x)$
- Verify that the correct answer matches the solution
- Create questions that are solvable and educational
- DOUBLE-CHECK: The final simplified expression in the solution MUST match the correct answer
- MATHEMATICAL CONSISTENCY: The solution steps must lead to the marked correct answer
- STEP-BY-STEP ACCURACY: Each step must be mathematically sound
- NO FORCED EQUALITIES: Do not force incorrect mathematical equalities

SOLUTION GUIDELINES:
- Show each step clearly and accurately
- Use proper mathematical notation throughout
- Verify each calculation before proceeding
- The final result must equal the correct answer
- If your calculation leads to a different result, check your work
- Do not force incorrect equalities like "3^(4-4n) = 3^(6-3n)"
- VERIFY EACH STEP: After each calculation, verify it leads toward the correct answer

MATHEMATICAL VALIDATION CHECKLIST:
- [ ] All exponent calculations are correct: (a^b)^c = a^(b*c)
- [ ] All division calculations are correct: a^m / a^n = a^(m-n)
- [ ] All algebraic simplifications are correct
- [ ] The final expression equals the correct answer exactly
- [ ] No mathematical errors in any step
- [ ] Each step logically leads to the next
- [ ] The calculated answer exists in the provided options
- [ ] The marked correct answer matches the calculated result

CRITICAL MATHEMATICAL RULES:
- (a^b)^c = a^(b*c) - Power of a power
- a^m * a^n = a^(m+n) - Multiplication of powers
- a^m / a^n = a^(m-n) - Division of powers
- (a*b)^n = a^n * b^n - Power of a product
- Double-check all exponent calculations
- Verify that your final answer is one of the provided options

BASIC ARITHMETIC ACCURACY:
- 12 = 6 × 2 (NOT 6^2 = 36)
- 6 × 2 = 12 (NOT 6^2 = 36)
- 3 × 4 = 12 (NOT 3^4 = 81)
- 2 × 6 = 12 (NOT 2^6 = 64)
- ALWAYS verify basic arithmetic: multiplication vs exponentiation
- NEVER confuse multiplication (×) with exponentiation (^)
- Check that 12 = 6 × 2, NOT 12 = 6^2 = 36

COMPREHENSIVE CALCULATION GUIDELINES:

## 1. FUNDAMENTAL PRINCIPLES

### 1.1 Arithmetic Accuracy
**Core Operations:**
- Addition: a + b = c
- Subtraction: a - b = c  
- Multiplication: a × b = ab
- Division: a ÷ b = a/b
- Exponentiation: a^n = a × a × ... × a (n times)

**Order of Operations (PEMDAS/BODMAS):**
1. Parentheses/Brackets ( )
2. Exponents/Orders ^, √
3. Multiplication & Division ×, ÷ (left-to-right)
4. Addition & Subtraction +, - (left-to-right)

**Example:**
3 + 4 × 2^2 = 3 + (4 × 4) = 19

### 1.2 Fraction & Decimal Rules
**Simplification:**
8/12 = 2/3 (divide by GCD 4)

**Conversion:**
0.75 = 3/4, 0.333... = 1/3

## 2. ALGEBRAIC COMPUTATIONS

### 2.1 Equation Solving
**Linear Equations:**
2x + 5 = 13
Step 1: 2x = 8
Step 2: x = 4
Verify: 2(4) + 5 = 13 ✓

**Quadratic Equations:**
ax^2 + bx + c = 0
x = (-b ± √(b^2 - 4ac))/(2a)

**Discriminant D = b^2 - 4ac:**
- D > 0: Two real roots
- D = 0: One real root  
- D < 0: No real roots

### 2.2 Polynomial Operations
**Factorization:**
a^2 - b^2 = (a + b)(a - b)
x^2 + 5x + 6 = (x + 2)(x + 3)

**Expansion:**
(a + b)^2 = a^2 + 2ab + b^2

## 3. GEOMETRIC CALCULATIONS

### 3.1 Key Formulas
**Pythagorean Theorem:**
c = √(a^2 + b^2) (Right triangles)

**Circle Properties:**
Area = πr^2, Circumference = 2πr

### 3.2 Angle Relationships
**Triangle Angles:**
∠A + ∠B + ∠C = 180°

**Parallel Lines:**
- Corresponding angles equal
- Alternate angles equal

## 4. TRIGONOMETRIC RULES

### 4.1 Basic Identities
sin^2θ + cos^2θ = 1
tanθ = sinθ/cosθ

### 4.2 Common Values
sin(30°) = 1/2, cos(45°) = √2/2

## 5. CALCULUS FOUNDATIONS

### 5.1 Derivatives
**Power Rule:**
d/dx(x^n) = nx^(n-1)

**Chain Rule:**
d/dx[f(g(x))] = f'(g(x))·g'(x)

### 5.2 Integrals
**Polynomial Integration:**
∫x^n dx = x^(n+1)/(n+1) + C (n ≠ -1)

## 6. PROBABILITY & STATISTICS

### 6.1 Probability Rules
P(A ∪ B) = P(A) + P(B) - P(A ∩ B)

### 6.2 Statistical Measures
**Mean:**
x̄ = Σx_i/n

## 7. VALIDATION PROTOCOLS

### 7.1 Step Verification
- Recalculate each operation independently
- Check unit consistency (e.g., area in cm^2)
- Verify against alternative methods

### 7.2 Final Checks
- Compare with graphical solutions
- Test edge cases (e.g., x=0, x=1)

## 8. COMMON ERROR PREVENTION

### 8.1 Critical Checks
**Exponent Errors:**
(2x)^2 = 4x^2 (not 2x^2)

**Angle Mode:**
Confirm calculator is in DEG/RAD as needed

### 8.2 Arithmetic Traps
12 = 6 × 2 (NOT 6^2 = 36)
a/(b+c) ≠ a/b + a/c

TOPIC-SPECIFIC VALIDATION RULES:

01. NUMBER SYSTEM & APPROXIMATION:
- Verify decimal place calculations
- Check rounding rules: 0.5 rounds up
- Validate scientific notation: a * 10^n where 1 ≤ |a| < 10
- Confirm significant figures counting
- Cross-check approximation bounds

02. IDENTITIES:
- Verify algebraic identity applications
- Check factor expansions: (a+b)^2 = a^2 + 2ab + b^2
- Validate difference of squares: a^2 - b^2 = (a+b)(a-b)
- Confirm trigonometric identities
- Test identity by substituting values

03. SIMULTANEOUS LINEAR EQUATIONS:
- Verify elimination steps maintain equation balance
- Check substitution accuracy
- Validate solution by substituting back into original equations
- Confirm coefficient calculations
- Test solution satisfies both equations

04. ANGLES, PARALLEL LINES, TRIANGLES & QUADRILATERALS:
- Verify angle sum properties: triangle = 180°, quadrilateral = 360°
- Check parallel line angle relationships
- Validate similar triangle ratios
- Confirm Pythagorean theorem applications: a^2 + b^2 = c^2
- Test congruency conditions

05. PERCENTAGES:
- Verify percentage calculations: (part/whole) * 100%
- Check percentage increase/decrease formulas
- Validate compound percentage calculations
- Confirm percentage of percentage calculations
- Test reverse percentage problems

06. TRANSFORMATION & SYMMETRY:
- Verify transformation matrix applications
- Check coordinate transformation accuracy
- Validate reflection, rotation, translation calculations
- Confirm symmetry line equations
- Test inverse transformations

07. INDICES, EXPONENTIAL & LOGARITHMIC FUNCTIONS:
- Verify logarithm base conversions: log_a(x) = ln(x)/ln(a)
- Check exponential growth/decay formulas
- Validate logarithm properties: log(ab) = log(a) + log(b)
- Confirm exponential equation solutions
- Test logarithmic equation domain restrictions

08. QUADRATIC EQUATIONS & GRAPHS:
- Verify discriminant calculations: b^2 - 4ac
- Check quadratic formula applications: x = (-b ± √(b^2-4ac))/(2a)
- Validate completing the square steps
- Confirm vertex form conversions
- Test factorization accuracy

09. POLYNOMIALS, REMAINDER & FACTOR THEOREM:
- Verify polynomial long division steps
- Check remainder theorem: P(a) = remainder when P(x) ÷ (x-a)
- Validate factor theorem applications
- Confirm synthetic division calculations
- Test polynomial factorization

10. INEQUALITIES & LINEAR PROGRAMMING:
- Verify inequality sign changes when multiplying/dividing by negatives
- Check feasible region boundaries
- Validate objective function optimization
- Confirm constraint satisfaction
- Test corner point evaluations

11. RATES, RATIOS & VARIATIONS:
- Verify ratio simplifications
- Check proportion cross-multiplication
- Validate direct/inverse variation constants
- Confirm rate unit conversions
- Test variation equation applications

12. ARITHMETIC & GEOMETRIC SEQUENCES:
- Verify arithmetic sequence formula: a_n = a_1 + (n-1)d
- Check geometric sequence formula: a_n = a_1 * r^(n-1)
- Validate sum formulas for both sequences
- Confirm common difference/ratio calculations
- Test sequence term identification

13. PROPERTIES OF CIRCLES:
- Verify circle theorems applications
- Check angle in semicircle = 90°
- Validate tangent-radius perpendicularity
- Confirm chord and arc relationships
- Test inscribed angle theorems

14. MENSURATION:
- Verify area formula applications
- Check volume calculation accuracy
- Validate surface area calculations
- Confirm unit conversions
- Test composite shape calculations

15. TRIGONOMETRY:
- Verify trigonometric ratio calculations
- Check SOHCAHTOA applications
- Validate special angle values
- Confirm trigonometric identity usage
- Test angle measurement conversions (degrees/radians)

16. TRIGONOMETRY IN 2D & 3D PROBLEMS:
- Verify sine rule: a/sin(A) = b/sin(B) = c/sin(C)
- Check cosine rule: c^2 = a^2 + b^2 - 2ab*cos(C)
- Validate 3D angle calculations
- Confirm vector component calculations
- Test bearing and navigation problems

17. EQUATIONS OF STRAIGHT LINES:
- Verify slope calculations: m = (y2-y1)/(x2-x1)
- Check point-slope form: y - y1 = m(x - x1)
- Validate parallel/perpendicular line conditions
- Confirm intercept calculations
- Test distance between parallel lines

18. EQUATIONS OF CIRCLES:
- Verify standard form: (x-h)^2 + (y-k)^2 = r^2
- Check general form expansions
- Validate center and radius identification
- Confirm tangent line equations
- Test circle intersection calculations

19. LOCUS:
- Verify locus condition translations
- Check distance formula applications
- Validate parametric representations
- Confirm geometric locus properties
- Test locus equation derivations

20. PERMUTATIONS & COMBINATIONS:
- Verify factorial calculations
- Check permutation formula: P(n,r) = n!/(n-r)!
- Validate combination formula: C(n,r) = n!/(r!(n-r)!)
- Confirm arrangement vs selection distinctions
- Test repetition and restriction problems

21. PROBABILITY:
- Verify probability sum = 1
- Check conditional probability: P(A|B) = P(A∩B)/P(B)
- Validate independent event calculations
- Confirm tree diagram probabilities
- Test expected value calculations

22. STATISTICS:
- Verify mean, median, mode calculations
- Check standard deviation formula applications
- Validate frequency table calculations
- Confirm correlation coefficient interpretations
- Test hypothesis testing procedures

23. CROSS-TOPIC PROBLEMS:
- Verify integration of multiple concepts
- Check consistency across different approaches
- Validate problem decomposition strategies
- Confirm logical connection between steps
- Test final answer against all given conditions

CRITICAL VALIDATION CHECKLIST:
BEFORE FINAL ANSWER:
- Re-calculate each arithmetic operation
- Verify all exponent rule applications
- Check unit consistency throughout
- Confirm answer matches one of the given options
- Test solution by substitution where applicable
- Validate domain and range restrictions
- Check for extraneous solutions
- Verify logical consistency of the solution path

ERROR PREVENTION PROTOCOLS:
- NEVER assume arithmetic without verification
- ALWAYS double-check exponent calculations
- CONSISTENTLY verify algebraic manipulations
- REPEATEDLY test intermediate results
- SYSTEMATICALLY check final answer validity

LaTeX FORMATTING STANDARDS:
- Fractions: $\\frac{numerator}{denominator}$
- Powers: $x^2$, $x^{n+1}$
- Roots: $\\sqrt{x}$, $\\sqrt[n]{x}$
- Trigonometric: $\\sin(x)$, $\\cos(\\theta)$, $\\tan(45°)$
- Greek letters: $\\alpha$, $\\beta$, $\\theta$, $\\pi$
- Symbols: $\\leq$, $\\geq$, $\\neq$, $\\approx$, $\\infty$

PROGRAMMING FORMAT OUTPUT:
- Use ^ for exponentiation (not ** or other symbols)
- Use * for multiplication explicitly
- Use / for division
- Use sqrt() for square roots
- Use parentheses () for grouping
- Example: (2^3 * 5) / sqrt(16) = (8 * 5) / 4 = 40 / 4 = 10

IMPORTANT: DO NOT use placeholder text like "Mock question text" or "Option A". Generate REAL mathematical content.
CRITICAL: After writing the solution, verify that the final expression equals the correct answer.`;
  }

  createRealDrillPrompt(originalQuestion, numberOfDrills) {
    const { question, topic, difficulty, options, correctAnswer, solution } = originalQuestion;
    
    // Add randomization seed to ensure different questions each time
    const randomSeed = Math.floor(Math.random() * 10000);
    const variationInstructions = [
      "Use completely different numbers and scenarios",
      "Change the context and approach while keeping the same mathematical concept",
      "Create questions with different problem-solving strategies",
      "Use alternative mathematical representations",
      "Vary the complexity and presentation style"
    ];
    const selectedVariation = variationInstructions[randomSeed % variationInstructions.length];
    
    return `You are an expert HKDSE Mathematics teacher. Create ${numberOfDrills} REAL mathematical drill questions based on this HKDSE Mathematics question.

VARIATION INSTRUCTION: ${selectedVariation}
RANDOMIZATION SEED: ${randomSeed}

ORIGINAL QUESTION:
Question: ${question}
Topic: ${topic}
Difficulty: ${difficulty}
Options: ${JSON.stringify(options)}
Correct Answer: ${correctAnswer}
Solution: ${solution}

CRITICAL REQUIREMENTS:
1. Generate REAL mathematical questions with actual content
2. Use the SAME topic and difficulty level as the original
3. Create similar question patterns but with different numbers
4. Include detailed, step-by-step solutions
5. Use proper LaTeX formatting for all mathematical expressions
6. Ensure all calculations are mathematically correct
7. Choose realistic numbers appropriate for the topic
8. MATHEMATICAL ACCURACY: The final expression in your solution MUST match the correct answer exactly

LaTeX FORMATTING REQUIREMENTS:
- Fractions: $\\frac{numerator}{denominator}$
- Powers: $x^2$, $x^{n+1}$
- Square roots: $\\sqrt{x}$, $\\sqrt{x^2 + y^2}$
- Trigonometric functions: $\\sin(x)$, $\\cos(\\theta)$, $\\tan(45°)$
- Greek letters: $\\alpha$, $\\beta$, $\\theta$, $\\pi$
- Mathematical symbols: $\\leq$, $\\geq$, $\\neq$, $\\approx$

SOLUTION FORMAT REQUIREMENTS:
- Each step must be mathematically correct
- Show all intermediate steps clearly
- Use proper mathematical notation
- The final step must equal the correct answer exactly
- Do not force incorrect equalities
- If your calculation leads to a different result, check your work
- VERIFY: After writing each step, verify it leads to the correct answer

MATHEMATICAL VALIDATION:
- Double-check all exponent calculations
- Verify that (a^b)^c = a^(b*c) is applied correctly
- Ensure that a^m / a^n = a^(m-n) is applied correctly
- Check that all algebraic simplifications are correct
- The final result must match the correct answer exactly
- CRITICAL: The calculated answer must be one of the provided options

CRITICAL MATHEMATICAL RULES:
- (a^b)^c = a^(b*c) - Power of a power
- a^m * a^n = a^(m+n) - Multiplication of powers  
- a^m / a^n = a^(m-n) - Division of powers
- (a*b)^n = a^n * b^n - Power of a product
- Double-check all exponent calculations
- Verify that your final answer is one of the provided options

BASIC ARITHMETIC ACCURACY:
- 12 = 6 × 2 (NOT 6^2 = 36)
- 6 × 2 = 12 (NOT 6^2 = 36)
- 3 × 4 = 12 (NOT 3^4 = 81)
- 2 × 6 = 12 (NOT 2^6 = 64)
- ALWAYS verify basic arithmetic: multiplication vs exponentiation
- NEVER confuse multiplication (×) with exponentiation (^)
- Check that 12 = 6 × 2, NOT 12 = 6^2 = 36
- (12)^222 = (6 × 2)^222, NOT (6^2)^222 = 36^222

COMPREHENSIVE CALCULATION GUIDELINES:

## 1. FUNDAMENTAL PRINCIPLES

### 1.1 Arithmetic Accuracy
**Core Operations:**
- Addition: a + b = c
- Subtraction: a - b = c  
- Multiplication: a × b = ab
- Division: a ÷ b = a/b
- Exponentiation: a^n = a × a × ... × a (n times)

**Order of Operations (PEMDAS/BODMAS):**
1. Parentheses/Brackets ( )
2. Exponents/Orders ^, √
3. Multiplication & Division ×, ÷ (left-to-right)
4. Addition & Subtraction +, - (left-to-right)

**Example:**
3 + 4 × 2^2 = 3 + (4 × 4) = 19

### 1.2 Fraction & Decimal Rules
**Simplification:**
8/12 = 2/3 (divide by GCD 4)

**Conversion:**
0.75 = 3/4, 0.333... = 1/3

## 2. ALGEBRAIC COMPUTATIONS

### 2.1 Equation Solving
**Linear Equations:**
2x + 5 = 13
Step 1: 2x = 8
Step 2: x = 4
Verify: 2(4) + 5 = 13 ✓

**Quadratic Equations:**
ax^2 + bx + c = 0
x = (-b ± √(b^2 - 4ac))/(2a)

**Discriminant D = b^2 - 4ac:**
- D > 0: Two real roots
- D = 0: One real root  
- D < 0: No real roots

### 2.2 Polynomial Operations
**Factorization:**
a^2 - b^2 = (a + b)(a - b)
x^2 + 5x + 6 = (x + 2)(x + 3)

**Expansion:**
(a + b)^2 = a^2 + 2ab + b^2

## 3. GEOMETRIC CALCULATIONS

### 3.1 Key Formulas
**Pythagorean Theorem:**
c = √(a^2 + b^2) (Right triangles)

**Circle Properties:**
Area = πr^2, Circumference = 2πr

### 3.2 Angle Relationships
**Triangle Angles:**
∠A + ∠B + ∠C = 180°

**Parallel Lines:**
- Corresponding angles equal
- Alternate angles equal

## 4. TRIGONOMETRIC RULES

### 4.1 Basic Identities
sin^2θ + cos^2θ = 1
tanθ = sinθ/cosθ

### 4.2 Common Values
sin(30°) = 1/2, cos(45°) = √2/2

## 5. CALCULUS FOUNDATIONS

### 5.1 Derivatives
**Power Rule:**
d/dx(x^n) = nx^(n-1)

**Chain Rule:**
d/dx[f(g(x))] = f'(g(x))·g'(x)

### 5.2 Integrals
**Polynomial Integration:**
∫x^n dx = x^(n+1)/(n+1) + C (n ≠ -1)

## 6. PROBABILITY & STATISTICS

### 6.1 Probability Rules
P(A ∪ B) = P(A) + P(B) - P(A ∩ B)

### 6.2 Statistical Measures
**Mean:**
x̄ = Σx_i/n

## 7. VALIDATION PROTOCOLS

### 7.1 Step Verification
- Recalculate each operation independently
- Check unit consistency (e.g., area in cm^2)
- Verify against alternative methods

### 7.2 Final Checks
- Compare with graphical solutions
- Test edge cases (e.g., x=0, x=1)

## 8. COMMON ERROR PREVENTION

### 8.1 Critical Checks
**Exponent Errors:**
(2x)^2 = 4x^2 (not 2x^2)

**Angle Mode:**
Confirm calculator is in DEG/RAD as needed

### 8.2 Arithmetic Traps
12 = 6 × 2 (NOT 6^2 = 36)
a/(b+c) ≠ a/b + a/c

TOPIC-SPECIFIC VALIDATION RULES:

01. NUMBER SYSTEM & APPROXIMATION:
- Verify decimal place calculations
- Check rounding rules: 0.5 rounds up
- Validate scientific notation: a * 10^n where 1 ≤ |a| < 10
- Confirm significant figures counting
- Cross-check approximation bounds

02. IDENTITIES:
- Verify algebraic identity applications
- Check factor expansions: (a+b)^2 = a^2 + 2ab + b^2
- Validate difference of squares: a^2 - b^2 = (a+b)(a-b)
- Confirm trigonometric identities
- Test identity by substituting values

03. SIMULTANEOUS LINEAR EQUATIONS:
- Verify elimination steps maintain equation balance
- Check substitution accuracy
- Validate solution by substituting back into original equations
- Confirm coefficient calculations
- Test solution satisfies both equations

04. ANGLES, PARALLEL LINES, TRIANGLES & QUADRILATERALS:
- Verify angle sum properties: triangle = 180°, quadrilateral = 360°
- Check parallel line angle relationships
- Validate similar triangle ratios
- Confirm Pythagorean theorem applications: a^2 + b^2 = c^2
- Test congruency conditions

05. PERCENTAGES:
- Verify percentage calculations: (part/whole) * 100%
- Check percentage increase/decrease formulas
- Validate compound percentage calculations
- Confirm percentage of percentage calculations
- Test reverse percentage problems

06. TRANSFORMATION & SYMMETRY:
- Verify transformation matrix applications
- Check coordinate transformation accuracy
- Validate reflection, rotation, translation calculations
- Confirm symmetry line equations
- Test inverse transformations

07. INDICES, EXPONENTIAL & LOGARITHMIC FUNCTIONS:
- Verify logarithm base conversions: log_a(x) = ln(x)/ln(a)
- Check exponential growth/decay formulas
- Validate logarithm properties: log(ab) = log(a) + log(b)
- Confirm exponential equation solutions
- Test logarithmic equation domain restrictions

08. QUADRATIC EQUATIONS & GRAPHS:
- Verify discriminant calculations: b^2 - 4ac
- Check quadratic formula applications: x = (-b ± √(b^2-4ac))/(2a)
- Validate completing the square steps
- Confirm vertex form conversions
- Test factorization accuracy

09. POLYNOMIALS, REMAINDER & FACTOR THEOREM:
- Verify polynomial long division steps
- Check remainder theorem: P(a) = remainder when P(x) ÷ (x-a)
- Validate factor theorem applications
- Confirm synthetic division calculations
- Test polynomial factorization

10. INEQUALITIES & LINEAR PROGRAMMING:
- Verify inequality sign changes when multiplying/dividing by negatives
- Check feasible region boundaries
- Validate objective function optimization
- Confirm constraint satisfaction
- Test corner point evaluations

11. RATES, RATIOS & VARIATIONS:
- Verify ratio simplifications
- Check proportion cross-multiplication
- Validate direct/inverse variation constants
- Confirm rate unit conversions
- Test variation equation applications

12. ARITHMETIC & GEOMETRIC SEQUENCES:
- Verify arithmetic sequence formula: a_n = a_1 + (n-1)d
- Check geometric sequence formula: a_n = a_1 * r^(n-1)
- Validate sum formulas for both sequences
- Confirm common difference/ratio calculations
- Test sequence term identification

13. PROPERTIES OF CIRCLES:
- Verify circle theorems applications
- Check angle in semicircle = 90°
- Validate tangent-radius perpendicularity
- Confirm chord and arc relationships
- Test inscribed angle theorems

14. MENSURATION:
- Verify area formula applications
- Check volume calculation accuracy
- Validate surface area calculations
- Confirm unit conversions
- Test composite shape calculations

15. TRIGONOMETRY:
- Verify trigonometric ratio calculations
- Check SOHCAHTOA applications
- Validate special angle values
- Confirm trigonometric identity usage
- Test angle measurement conversions (degrees/radians)

16. TRIGONOMETRY IN 2D & 3D PROBLEMS:
- Verify sine rule: a/sin(A) = b/sin(B) = c/sin(C)
- Check cosine rule: c^2 = a^2 + b^2 - 2ab*cos(C)
- Validate 3D angle calculations
- Confirm vector component calculations
- Test bearing and navigation problems

17. EQUATIONS OF STRAIGHT LINES:
- Verify slope calculations: m = (y2-y1)/(x2-x1)
- Check point-slope form: y - y1 = m(x - x1)
- Validate parallel/perpendicular line conditions
- Confirm intercept calculations
- Test distance between parallel lines

18. EQUATIONS OF CIRCLES:
- Verify standard form: (x-h)^2 + (y-k)^2 = r^2
- Check general form expansions
- Validate center and radius identification
- Confirm tangent line equations
- Test circle intersection calculations

19. LOCUS:
- Verify locus condition translations
- Check distance formula applications
- Validate parametric representations
- Confirm geometric locus properties
- Test locus equation derivations

20. PERMUTATIONS & COMBINATIONS:
- Verify factorial calculations
- Check permutation formula: P(n,r) = n!/(n-r)!
- Validate combination formula: C(n,r) = n!/(r!(n-r)!)
- Confirm arrangement vs selection distinctions
- Test repetition and restriction problems

21. PROBABILITY:
- Verify probability sum = 1
- Check conditional probability: P(A|B) = P(A∩B)/P(B)
- Validate independent event calculations
- Confirm tree diagram probabilities
- Test expected value calculations

22. STATISTICS:
- Verify mean, median, mode calculations
- Check standard deviation formula applications
- Validate frequency table calculations
- Confirm correlation coefficient interpretations
- Test hypothesis testing procedures

23. CROSS-TOPIC PROBLEMS:
- Verify integration of multiple concepts
- Check consistency across different approaches
- Validate problem decomposition strategies
- Confirm logical connection between steps
- Test final answer against all given conditions

CRITICAL VALIDATION CHECKLIST:
BEFORE FINAL ANSWER:
- Re-calculate each arithmetic operation
- Verify all exponent rule applications
- Check unit consistency throughout
- Confirm answer matches one of the given options
- Test solution by substitution where applicable
- Validate domain and range restrictions
- Check for extraneous solutions
- Verify logical consistency of the solution path

ERROR PREVENTION PROTOCOLS:
- NEVER assume arithmetic without verification
- ALWAYS double-check exponent calculations
- CONSISTENTLY verify algebraic manipulations
- REPEATEDLY test intermediate results
- SYSTEMATICALLY check final answer validity

LaTeX FORMATTING STANDARDS:
- Fractions: $\\frac{numerator}{denominator}$
- Powers: $x^2$, $x^{n+1}$
- Roots: $\\sqrt{x}$, $\\sqrt[n]{x}$
- Trigonometric: $\\sin(x)$, $\\cos(\\theta)$, $\\tan(45°)$
- Greek letters: $\\alpha$, $\\beta$, $\\theta$, $\\pi$
- Symbols: $\\leq$, $\\geq$, $\\neq$, $\\approx$, $\\infty$

PROGRAMMING FORMAT OUTPUT:
- Use ^ for exponentiation (not ** or other symbols)
- Use * for multiplication explicitly
- Use / for division
- Use sqrt() for square roots
- Use parentheses () for grouping
- Example: (2^3 * 5) / sqrt(16) = (8 * 5) / 4 = 40 / 4 = 10

CRITICAL JSON FORMATTING REQUIREMENTS:
- You MUST respond with ONLY a valid JSON object
- Do not include any text, explanations, or formatting outside the JSON
- Do not include markdown formatting, code blocks, or any other text
- Start your response with { and end with }
- Ensure all quotes are properly escaped
- Do not include trailing commas
- Do not include duplicate opening or closing braces

RESPOND WITH THIS EXACT JSON FORMAT:
{
  "drillQuestions": [
    {
      "id": "drill_1",
      "questionNumber": 1,
      "question": "REAL mathematical question in LaTeX format",
      "options": [
        { "id": "A", "text": "REAL mathematical option in LaTeX" },
        { "id": "B", "text": "REAL mathematical option in LaTeX" },
        { "id": "C", "text": "REAL mathematical option in LaTeX" },
        { "id": "D", "text": "REAL mathematical option in LaTeX" }
      ],
      "correctAnswer": "A",
      "solution": "Step-by-step solution ending with the correct answer",
      "topic": "${topic}",
      "difficulty": "${difficulty}",
      "hasImage": false,
      "imageDescription": ""
    }
  ],
  "originalQuestionId": "${originalQuestion.id}",
  "totalDrills": ${numberOfDrills}
}

CRITICAL: Generate REAL mathematical content, NOT placeholder text. Each question should be a solvable mathematical problem with actual numbers and calculations. The final expression in your solution MUST match the correct answer exactly. RESPOND WITH ONLY THE JSON OBJECT.`;
  }

  validateAndFixDrillQuestions(drillData, originalQuestion) {
    if (!drillData || typeof drillData !== 'object') {
      throw new Error('API returned invalid data structure');
    }
    
    if (!drillData.drillQuestions || !Array.isArray(drillData.drillQuestions)) {
      console.error('Invalid drill data structure:', drillData);
      throw new Error('API response missing drill questions array');
    }
    
    if (drillData.drillQuestions.length === 0) {
      throw new Error('API generated no drill questions');
    }

    const validatedQuestions = drillData.drillQuestions.map((drill, index) => {
      // Validate and fix each question
      const validatedDrill = this.validateAndFixSingleDrill(drill, index + 1, originalQuestion);
      return validatedDrill;
    });

    return {
      drillQuestions: validatedQuestions,
      originalQuestionId: drillData.originalQuestionId || originalQuestion.id,
      totalDrills: validatedQuestions.length,
      metadata: {
        ...drillData.metadata,
        validationApplied: true,
        validationTimestamp: new Date().toISOString(),
        isRealAIGenerated: true
      }
    };
  }

  validateAndFixSingleDrill(drill, questionNumber, originalQuestion) {
    if (!drill || typeof drill !== 'object') {
      throw new Error(`Invalid drill question structure for question ${questionNumber}`);
    }
    
    // Ensure required fields exist
    const validatedDrill = {
      id: drill.id || `drill_${originalQuestion.id}_${questionNumber}`,
      questionNumber: drill.questionNumber || questionNumber,
      question: this.validateAndFixLatex(drill.question || ''),
      options: this.validateOptions(drill.options || []),
      correctAnswer: drill.correctAnswer || 'A',
      solution: this.validateAndFixLatex(drill.solution || ''),
      topic: drill.topic || originalQuestion.topic,
      difficulty: drill.difficulty || originalQuestion.difficulty,
      hasImage: drill.hasImage || false,
      imageDescription: drill.imageDescription || '',
      isDrillQuestion: true,
      originalQuestionId: originalQuestion.id,
      metadata: {
        generatedAt: new Date().toISOString(),
        isRealAIGenerated: true
      }
    };

    // Validate that correct answer exists in options
    const optionIds = validatedDrill.options.map(opt => opt.id);
    if (!optionIds.includes(validatedDrill.correctAnswer)) {
      validatedDrill.correctAnswer = optionIds[0] || 'A';
    }

    // Validate that question and solution are not empty
    if (!validatedDrill.question.trim()) {
      throw new Error(`Question ${questionNumber} has empty question text`);
    }
    
    if (!validatedDrill.solution.trim()) {
      throw new Error(`Question ${questionNumber} has empty solution`);
    }

    // Validate basic arithmetic accuracy first
    const arithmeticResult = this.validateBasicArithmeticAccuracy(validatedDrill.solution);
    if (arithmeticResult.hasErrors) {
      console.error(`CRITICAL ARITHMETIC ERRORS DETECTED in question ${questionNumber} - FIXING...`);
      validatedDrill.solution = arithmeticResult.correctedSolution;
    }

    // Validate topic-specific accuracy
    const topicResult = this.validateTopicSpecificAccuracy(validatedDrill);
    if (topicResult.hasErrors) {
      console.error(`TOPIC-SPECIFIC ERRORS DETECTED in question ${questionNumber}:`, topicResult.errors);
      // Log the errors for debugging
      topicResult.errors.forEach(error => {
        console.error(`  - ${error}`);
      });
    }

    // Validate mathematical consistency between solution and correct answer
    const mathematicalError = this.validateMathematicalConsistency(validatedDrill);
    if (mathematicalError) {
      console.warn(`Mathematical inconsistency in question ${questionNumber}:`, mathematicalError);
      // Fix the solution to match the correct answer
      validatedDrill.solution = this.fixMathematicalInconsistency(validatedDrill);
    }

    return validatedDrill;
  }

  validateBasicArithmeticAccuracy(solution) {
    const arithmeticErrors = [
      { pattern: /12\s*=\s*6\^2/g, correct: '12 = 6 × 2', description: '12 = 6^2 should be 12 = 6 × 2' },
      { pattern: /6\s*\*\s*2\s*=\s*6\^2/g, correct: '6 × 2 = 12', description: '6 × 2 = 6^2 should be 6 × 2 = 12' },
      { pattern: /3\s*\*\s*4\s*=\s*3\^4/g, correct: '3 × 4 = 12', description: '3 × 4 = 3^4 should be 3 × 4 = 12' },
      { pattern: /2\s*\*\s*6\s*=\s*2\^6/g, correct: '2 × 6 = 12', description: '2 × 6 = 2^6 should be 2 × 6 = 12' },
      { pattern: /\(6\^2\)\^222/g, correct: '(6 × 2)^222', description: '(6^2)^222 should be (6 × 2)^222' },
      { pattern: /\(12\)\^222\s*=\s*\(6\^2\)\^222/g, correct: '(12)^222 = (6 × 2)^222', description: '(12)^222 = (6^2)^222 should be (12)^222 = (6 × 2)^222' },
      // Specific patterns for the exact error shown
      { pattern: /\(12\)\^222\s*=\s*\(6\^2\)\^222/g, correct: '(12)^222 = (6 × 2)^222', description: '(12)^222 = (6^2)^222 should be (12)^222 = (6 × 2)^222' },
      { pattern: /\(6\^2\)\^222\s*=\s*6\^444/g, correct: '(6 × 2)^222 = 12^222', description: '(6^2)^222 = 6^444 should be (6 × 2)^222 = 12^222' },
      { pattern: /12\s*=\s*6\^2\s*=\s*36/g, correct: '12 = 6 × 2', description: '12 = 6^2 = 36 should be 12 = 6 × 2' },
      { pattern: /\(6\^2\)\^222\s*=\s*36\^222/g, correct: '(6 × 2)^222 = 12^222', description: '(6^2)^222 = 36^222 should be (6 × 2)^222 = 12^222' }
    ];

    let hasErrors = false;
    let correctedSolution = solution;

    arithmeticErrors.forEach(error => {
      if (error.pattern.test(solution)) {
        console.error(`ARITHMETIC ERROR DETECTED: ${error.description}`);
        console.error(`Found: ${solution.match(error.pattern)[0]}`);
        console.error(`Should be: ${error.correct}`);
        hasErrors = true;
        
        // Replace the error with the correct expression
        correctedSolution = correctedSolution.replace(error.pattern, error.correct);
      }
    });

    return { hasErrors, correctedSolution };
  }

  validateTopicSpecificAccuracy(drill) {
    const topic = drill.topic?.toLowerCase() || '';
    const solution = drill.solution.toLowerCase();
    let errors = [];

    // Topic-specific validation rules based on DeepSeek Calculation Guidelines
    const topicValidations = {
      'number system': () => {
        // Check for scientific notation errors
        if (solution.includes('10^') && !solution.match(/10\^[0-9]+/)) {
          errors.push('Invalid scientific notation format');
        }
        // Check for decimal place calculations
        if (solution.includes('decimal') && !solution.includes('.')) {
          errors.push('Missing decimal point in decimal calculations');
        }
      },
      'identities': () => {
        // Check for identity expansion errors
        if (solution.includes('(a+b)^2') && !solution.includes('a^2 + 2ab + b^2')) {
          errors.push('Incorrect identity expansion: (a+b)^2 = a^2 + 2ab + b^2');
        }
        // Check for difference of squares
        if (solution.includes('a^2 - b^2') && !solution.includes('(a+b)(a-b)')) {
          errors.push('Incorrect factorization: a^2 - b^2 = (a+b)(a-b)');
        }
      },
      'simultaneous': () => {
        // Check for equation balance
        if (solution.includes('=') && solution.split('=').length < 2) {
          errors.push('Incomplete equation');
        }
        // Check for substitution verification
        if (solution.includes('x =') && !solution.includes('verify')) {
          errors.push('Missing substitution verification');
        }
      },
      'angles': () => {
        // Check for angle sum properties
        if (solution.includes('triangle') && !solution.includes('180')) {
          errors.push('Missing triangle angle sum property: ∠A + ∠B + ∠C = 180°');
        }
        // Check for parallel line properties
        if (solution.includes('parallel') && !solution.includes('corresponding')) {
          errors.push('Missing parallel line angle relationships');
        }
      },
      'percentages': () => {
        // Check for percentage calculation errors
        if (solution.includes('%') && !solution.includes('100')) {
          errors.push('Incorrect percentage calculation: (part/whole) * 100%');
        }
        // Check for percentage increase/decrease
        if (solution.includes('increase') && !solution.includes('original')) {
          errors.push('Missing original value in percentage increase calculation');
        }
      },
      'indices': () => {
        // Check for exponent rule violations
        if (solution.includes('^') && solution.includes('*') && !solution.includes('+')) {
          errors.push('Incorrect exponent rule application: a^m * a^n = a^(m+n)');
        }
        // Check for power of power rule
        if (solution.includes('(a^b)^c') && !solution.includes('a^(b*c)')) {
          errors.push('Incorrect power of power rule: (a^b)^c = a^(b*c)');
        }
      },
      'quadratic': () => {
        // Check for quadratic formula
        if (solution.includes('x =') && !solution.includes('±')) {
          errors.push('Missing quadratic formula: x = (-b ± √(b^2-4ac))/(2a)');
        }
        // Check for discriminant
        if (solution.includes('discriminant') && !solution.includes('b^2 - 4ac')) {
          errors.push('Missing discriminant calculation: D = b^2 - 4ac');
        }
      },
      'trigonometry': () => {
        // Check for trigonometric function usage
        if (solution.includes('sin') || solution.includes('cos') || solution.includes('tan')) {
          if (!solution.includes('(') || !solution.includes(')')) {
            errors.push('Incorrect trigonometric function notation');
          }
        }
        // Check for basic identities
        if (solution.includes('sin^2') && solution.includes('cos^2') && !solution.includes('= 1')) {
          errors.push('Missing trigonometric identity: sin^2θ + cos^2θ = 1');
        }
      },
      'probability': () => {
        // Check for probability sum
        if (solution.includes('probability') && !solution.includes('1')) {
          errors.push('Missing probability validation: sum of probabilities = 1');
        }
        // Check for conditional probability
        if (solution.includes('conditional') && !solution.includes('P(A|B)')) {
          errors.push('Missing conditional probability formula: P(A|B) = P(A∩B)/P(B)');
        }
      },
      'statistics': () => {
        // Check for statistical calculations
        if (solution.includes('mean') || solution.includes('median')) {
          if (!solution.includes('sum') && !solution.includes('count')) {
            errors.push('Incomplete statistical calculation');
          }
        }
        // Check for standard deviation
        if (solution.includes('standard deviation') && !solution.includes('√')) {
          errors.push('Missing square root in standard deviation calculation');
        }
      },
      'geometry': () => {
        // Check for Pythagorean theorem
        if (solution.includes('right triangle') && !solution.includes('a^2 + b^2 = c^2')) {
          errors.push('Missing Pythagorean theorem: a^2 + b^2 = c^2');
        }
        // Check for circle properties
        if (solution.includes('circle') && !solution.includes('πr^2')) {
          errors.push('Missing circle area formula: A = πr^2');
        }
      },
      'calculus': () => {
        // Check for derivative power rule
        if (solution.includes('derivative') && solution.includes('x^n') && !solution.includes('nx^(n-1)')) {
          errors.push('Missing power rule: d/dx(x^n) = nx^(n-1)');
        }
        // Check for chain rule
        if (solution.includes('chain rule') && !solution.includes('f\'(g(x))')) {
          errors.push('Missing chain rule: d/dx[f(g(x))] = f\'(g(x))·g\'(x)');
        }
      }
    };

    // Apply topic-specific validation
    for (const [topicKey, validationFn] of Object.entries(topicValidations)) {
      if (topic.includes(topicKey)) {
        validationFn();
      }
    }

    // Additional comprehensive checks based on DeepSeek Guidelines
    const comprehensiveChecks = {
      // Check for basic arithmetic errors
      arithmetic: () => {
        if (solution.includes('12 = 6^2') || solution.includes('6^2 = 12')) {
          errors.push('CRITICAL ARITHMETIC ERROR: 12 = 6 × 2, NOT 6^2 = 36');
        }
        if (solution.includes('3 × 4 = 3^4')) {
          errors.push('CRITICAL ARITHMETIC ERROR: 3 × 4 = 12, NOT 3^4 = 81');
        }
      },
      // Check for order of operations
      pemdas: () => {
        if (solution.includes('3 + 4 × 2^2') && solution.includes('= 28')) {
          errors.push('ORDER OF OPERATIONS ERROR: 3 + 4 × 2^2 = 19, NOT 28');
        }
      },
      // Check for fraction simplification
      fractions: () => {
        if (solution.includes('8/12') && !solution.includes('2/3')) {
          errors.push('FRACTION SIMPLIFICATION ERROR: 8/12 = 2/3');
        }
      },
      // Check for exponent rules
      exponents: () => {
        if (solution.includes('(2x)^2') && solution.includes('= 2x^2')) {
          errors.push('EXPONENT ERROR: (2x)^2 = 4x^2, NOT 2x^2');
        }
      }
    };

    // Apply comprehensive checks
    Object.values(comprehensiveChecks).forEach(check => check());

    return {
      hasErrors: errors.length > 0,
      errors: errors,
      correctedSolution: drill.solution // For now, return original solution
    };
  }

  validateMathematicalConsistency(drill) {
    try {
      // Extract the final expression from the solution
      const solutionLines = drill.solution.split('\n');
      const finalExpression = this.extractFinalExpression(solutionLines);
      
      if (!finalExpression) {
        return 'Could not extract final expression from solution';
      }

      // Get the correct answer text
      const correctOption = drill.options.find(opt => opt.id === drill.correctAnswer);
      if (!correctOption) {
        return 'Could not find correct answer option';
      }

      const correctAnswerText = correctOption.text.trim();
      
      // Check if the final expression matches the correct answer
      if (finalExpression !== correctAnswerText) {
        return `Mathematical inconsistency: Solution ends with "${finalExpression}" but correct answer is "${correctAnswerText}"`;
      }

      // Additional validation: Check if the calculated answer exists in the options
      const optionTexts = drill.options.map(opt => opt.text.trim());
      if (!optionTexts.includes(finalExpression)) {
        return `Calculated answer "${finalExpression}" is not in the provided options: [${optionTexts.join(', ')}]`;
      }

      return null; // No error
    } catch (error) {
      return `Error validating mathematical consistency: ${error.message}`;
    }
  }

  extractFinalExpression(solutionLines) {
    // Look for the final expression in the solution
    for (let i = solutionLines.length - 1; i >= 0; i--) {
      const line = solutionLines[i].trim();
      
      // Skip empty lines
      if (!line) continue;
      
      // Look for patterns like "= expression" or "expression"
      const match = line.match(/=\s*([^=]+)$/);
      if (match) {
        const expression = match[1].trim();
        // Only return if it looks like a mathematical expression
        if (expression.match(/^[a-zA-Z0-9^()+\-*/\\frac{}]+$/)) {
          return expression;
        }
      }
      
      // Look for standalone expressions that might be the final answer
      if (line.match(/^[a-zA-Z0-9^()+\-*/\\frac{}]+$/)) {
        return line;
      }
      
      // Look for expressions in LaTeX format
      const latexMatch = line.match(/\\[a-zA-Z]+\([^)]+\)/);
      if (latexMatch) {
        return line;
      }
    }
    
    return null;
  }

  fixMathematicalInconsistency(drill) {
    const correctOption = drill.options.find(opt => opt.id === drill.correctAnswer);
    if (!correctOption) {
      return drill.solution;
    }

    const correctAnswerText = correctOption.text.trim();
    
    // Check if the calculated answer exists in the options
    const optionTexts = drill.options.map(opt => opt.text.trim());
    const finalExpression = this.extractFinalExpression(drill.solution.split('\n'));
    
    if (finalExpression && !optionTexts.includes(finalExpression)) {
      // The calculated answer is not in the options - this is a serious error
      console.error(`Serious mathematical error: Calculated answer "${finalExpression}" not in options: [${optionTexts.join(', ')}]`);
      
      // Try to find the option that most closely matches the calculated answer
      const bestMatch = this.findBestMathematicalMatch(finalExpression, optionTexts);
      if (bestMatch) {
        console.log(`Attempting to fix by using best match: ${bestMatch}`);
        return this.fixSolutionToMatchAnswer(drill.solution, bestMatch);
      }
    }
    
    // If the calculated answer exists in options but doesn't match the marked correct answer
    if (finalExpression && optionTexts.includes(finalExpression) && finalExpression !== correctAnswerText) {
      console.warn(`Solution calculates "${finalExpression}" but marks "${correctAnswerText}" as correct`);
      return this.fixSolutionToMatchAnswer(drill.solution, correctAnswerText);
    }
    
    return drill.solution;
  }

  findBestMathematicalMatch(calculatedAnswer, options) {
    // Simple heuristic to find the best match
    // This is a basic implementation - could be enhanced with more sophisticated matching
    for (const option of options) {
      if (option.includes(calculatedAnswer) || calculatedAnswer.includes(option)) {
        return option;
      }
    }
    return null;
  }

  fixSolutionToMatchAnswer(solution, correctAnswer) {
    const solutionLines = solution.split('\n');
    const correctedLines = [];
    let foundError = false;
    
    for (let i = 0; i < solutionLines.length; i++) {
      const line = solutionLines[i].trim();
      
      // Look for lines that contain "=" and might be the final step
      if (line.includes('=') && !foundError) {
        // Check if this line contains a mathematical expression that doesn't match the correct answer
        const match = line.match(/=\s*([^=]+)$/);
        if (match) {
          const expression = match[1].trim();
          if (expression.match(/^[a-zA-Z0-9^()+\-*/\\frac{}]+$/) && expression !== correctAnswer) {
            // Replace the incorrect expression with the correct one
            const correctedLine = line.replace(expression, correctAnswer);
            correctedLines.push(correctedLine);
            foundError = true;
            continue;
          }
        }
      }
      
      correctedLines.push(line);
    }
    
    // If we didn't find an error to fix, just return the original solution
    if (!foundError) {
      return solution;
    }
    
    return correctedLines.join('\n');
  }

  validateAndFixLatex(text) {
    if (!text || typeof text !== 'string') return '';
    
    // Fix common LaTeX issues
    let fixedText = text
      // Ensure proper LaTeX delimiters
      .replace(/([^$])\$([^$]+)\$([^$])/g, '$1$$$2$$$3')
      .replace(/^([^$]+)$/, '$$$1$$')
      // Fix common LaTeX syntax errors
      .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '\\frac{$1}{$2}')
      .replace(/\\sqrt\{([^}]+)\}/g, '\\sqrt{$1}')
      .replace(/([a-zA-Z0-9\)])\^([a-zA-Z0-9])/g, '$1^{$2}')
      .replace(/([a-zA-Z0-9\)])_([a-zA-Z0-9])/g, '$1_{$2}')
      // Fix trigonometric functions
      .replace(/sin\(/g, '\\sin(')
      .replace(/cos\(/g, '\\cos(')
      .replace(/tan\(/g, '\\tan(')
      // Fix common symbols
      .replace(/<=/g, '\\leq')
      .replace(/>=/g, '\\geq')
      .replace(/!=/g, '\\neq')
      .replace(/pi/g, '\\pi')
      .replace(/theta/g, '\\theta')
      .replace(/alpha/g, '\\alpha')
      .replace(/beta/g, '\\beta');

    return fixedText;
  }

  validateOptions(options) {
    if (!Array.isArray(options) || options.length === 0) {
      throw new Error('Invalid options structure from API');
    }

    // Ensure all options have required fields
    return options.map((option, index) => ({
      id: option.id || String.fromCharCode(65 + index), // A, B, C, D
      text: this.validateAndFixLatex(option.text || `Option ${String.fromCharCode(65 + index)}`)
    }));
  }

  estimateTokens(originalQuestion, numberOfDrills) {
    // Estimate tokens based on question complexity and number of drills
    const questionLength = originalQuestion.question.length;
    const solutionLength = originalQuestion.solution.length;
    const optionsLength = originalQuestion.options.reduce((sum, opt) => sum + opt.text.length, 0);
    
    // Base tokens for prompt + response
    const baseTokens = 800; // Increased for enhanced prompts
    const questionTokens = Math.ceil((questionLength + solutionLength + optionsLength) / 4);
    const drillTokens = numberOfDrills * 300; // Increased for more detailed solutions
    
    return baseTokens + questionTokens + drillTokens;
  }

  calculateCost(tokensUsed, usage) {
    if (!usage) return 0;
    
    const inputTokens = usage.prompt_tokens || 0;
    const outputTokens = usage.completion_tokens || 0;
    
    // DeepSeek API pricing (in RMB per million tokens)
    // Standard time pricing (Beijing time 08:30-00:30)
    const standardInputPrice = 2.0; // Standard time, cache miss (deepseek-chat)
    const standardOutputPrice = 8.0; // Standard time (deepseek-chat)
    
    // Discount time pricing (Beijing time 00:30-08:30) - 50% discount
    const currentHour = new Date().getHours();
    const isDiscountTime = currentHour >= 0 && currentHour < 8.5;
    
    const finalInputPrice = isDiscountTime ? standardInputPrice * 0.5 : standardInputPrice;
    const finalOutputPrice = isDiscountTime ? standardOutputPrice * 0.5 : standardOutputPrice;
    
    const inputCost = (inputTokens / 1000000) * finalInputPrice;
    const outputCost = (outputTokens / 1000000) * finalOutputPrice;
    
    return inputCost + outputCost;
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

const deepSeekDrillApiService = new DeepSeekDrillApiService();
export default deepSeekDrillApiService; 